import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, Bookmark, LogOut, PenLine, Trophy, Users, Zap, User as UserIcon, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { powerMyProfile, powerMyBookmarks, powerMyCreated, powerMySigned, powerWallet, powerBookmarkToggle } from "@/lib/powerApi";
import PetitionCard from "@/components/power/PetitionCard";
import PowerShareDialog from "@/components/power/PowerShareDialog";

const TABS = [
  { id: "created",   label: "Petitions I made" },
  { id: "signed",    label: "Petitions I signed" },
  { id: "bookmarks", label: "Bookmarks" },
];

export default function PowerProfile() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [tab, setTab] = useState("created");
  const [items, setItems] = useState([]);
  const [sharing, setSharing] = useState(null);
  const [bookmarks, setBookmarks] = useState({});

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login?next=/power/profile"); return; }
    powerMyProfile().then(setProfile).catch(() => {});
    powerWallet().then(setWallet).catch(() => {});
  }, [user, loading, nav]);

  useEffect(() => {
    if (!user) return;
    const loader = tab === "created" ? powerMyCreated : tab === "signed" ? powerMySigned : powerMyBookmarks;
    loader().then((r) => setItems(r.petitions || [])).catch(() => setItems([]));
    if (tab === "bookmarks") {
      // Everything in this tab is bookmarked by definition
      powerMyBookmarks().then((r) => {
        const map = {}; (r.petitions || []).forEach((p) => { map[p.slug] = true; });
        setBookmarks(map);
      }).catch(() => {});
    }
  }, [tab, user]);

  const toggleBookmark = async (p) => {
    try {
      const r = await powerBookmarkToggle(p.slug);
      setBookmarks((b) => ({ ...b, [p.slug]: r.bookmarked }));
      if (tab === "bookmarks" && !r.bookmarked) {
        setItems((list) => list.filter((x) => x.slug !== p.slug));
      }
      toast.success(r.bookmarked ? "Bookmarked" : "Removed from bookmarks");
    } catch { toast.error("Couldn't bookmark"); }
  };

  if (loading || !user) return null;

  return (
    <div data-testid="power-profile" className="pb-6 space-y-6">
      <section className="bg-white rounded-3xl border border-[#E4EAF3] p-6 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl text-white"
            style={{ background: profile?.user?.avatar_color || "#1E88FF" }}>
            {(profile?.user?.name || user.name || user.email)[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-black text-2xl text-[#0A1A2F] inline-flex items-center gap-1.5">
              {profile?.user?.name || user.name || user.email?.split("@")[0]}
              {profile?.user?.power_verified && <BadgeCheck className="w-5 h-5 text-[#1E88FF]" fill="#1E88FF" stroke="white" />}
            </div>
            <div className="text-xs text-[#0A1A2F]/60 truncate">{user.email}</div>
          </div>
          <button data-testid="profile-logout" onClick={() => { logout(); toast.success("Logged out"); nav("/power"); }}
            className="w-10 h-10 rounded-full bg-[#F6F8FC] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] flex items-center justify-center text-[#0A1A2F]/60">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-5">
          <ProfileStat testId="stat-created" label="Created" value={profile?.stats?.petitions_created ?? 0} icon={<PenLine className="w-4 h-4" />} />
          <ProfileStat testId="stat-signed" label="Signed" value={profile?.stats?.petitions_signed ?? 0} icon={<UserIcon className="w-4 h-4" />} />
          <ProfileStat testId="stat-balance" label="Balance" value={wallet ? `$${wallet.balance_usd.toFixed(2)}` : "—"} icon={<Wallet className="w-4 h-4" />} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Link data-testid="profile-wallet-link" to="/power/wallet" className="inline-flex items-center gap-1.5 bg-[#1E88FF] text-white font-semibold text-sm px-4 py-2 rounded-full hover:bg-[#0A1A2F]">
            <Wallet className="w-4 h-4" /> Wallet
          </Link>
          <Link data-testid="profile-rewards-link" to="/power/rewards" className="inline-flex items-center gap-1.5 bg-white border border-[#E4EAF3] font-semibold text-sm px-4 py-2 rounded-full hover:border-[#1E88FF]">
            <Zap className="w-4 h-4 text-[#F59E0B]" fill="#F59E0B" /> Rewards
          </Link>
          <Link data-testid="profile-referrals-link" to="/power/referrals" className="inline-flex items-center gap-1.5 bg-white border border-[#E4EAF3] font-semibold text-sm px-4 py-2 rounded-full hover:border-[#1E88FF]">
            <Users className="w-4 h-4 text-[#1E88FF]" /> Invite
          </Link>
          <Link data-testid="profile-leaderboard-link" to="/power/leaderboard" className="inline-flex items-center gap-1.5 bg-white border border-[#E4EAF3] font-semibold text-sm px-4 py-2 rounded-full hover:border-[#1E88FF]">
            <Trophy className="w-4 h-4 text-[#F59E0B]" /> Leaderboard
          </Link>
          <Link to="/power/create" className="inline-flex items-center gap-1.5 bg-white border border-[#E4EAF3] font-semibold text-sm px-4 py-2 rounded-full hover:border-[#1E88FF]">
            <PenLine className="w-4 h-4" /> New petition
          </Link>
        </div>
      </section>

      <div className="flex gap-2 border-b border-[#E4EAF3]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`profile-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={"px-3 py-2 text-sm font-semibold transition-colors relative " + (tab === t.id ? "text-[#1E88FF]" : "text-[#0A1A2F]/50 hover:text-[#0A1A2F]")}>
            {t.label}
            {tab === t.id && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1E88FF]" />}
          </button>
        ))}
      </div>

      {items.length === 0 ? (
        <div data-testid="profile-empty" className="text-center py-10 text-[#0A1A2F]/50 text-sm">
          {tab === "created" ? "You haven't created a petition yet. Start one — it takes minutes."
            : tab === "signed" ? "No signatures yet. Explore petitions to find causes you care about."
              : <><Bookmark className="w-6 h-6 inline mr-1" /> Bookmark petitions to save them here.</>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {items.map((p) => (
            <PetitionCard
              key={p.slug}
              petition={p}
              onShare={setSharing}
              onBookmark={toggleBookmark}
              bookmarked={!!bookmarks[p.slug]}
            />
          ))}
        </div>
      )}

      {sharing && (
        <PowerShareDialog
          url={`${window.location.origin}/power/p/${sharing.slug}`}
          title={sharing.title}
          onClose={() => setSharing(null)}
        />
      )}
    </div>
  );
}

function ProfileStat({ label, value, icon, testId }) {
  return (
    <div data-testid={testId} className="bg-[#F6F8FC] rounded-xl p-3">
      <div className="text-[10px] font-bold uppercase tracking-widest text-[#0A1A2F]/60 inline-flex items-center gap-1">{icon}{label}</div>
      <div className="text-lg font-black text-[#0A1A2F] mt-0.5">{value}</div>
    </div>
  );
}
